import { type ElementType, type FC, type ReactNode, useId } from 'react';

import cn from 'classnames';

import type { CommunitySchedulePickerProps } from '../../CommunitySchedulePicker';
import { ComposerFrame, ComposerFrameBody, ComposerFrameFooter, ComposerFrameHeader } from '../layout/ComposerFrame';

export type CommunitySchedulePickerTranslate = (key: string, values?: Record<string, unknown>) => string;

interface SchedulePickerFrameProps {
  translate: CommunitySchedulePickerTranslate;
  titleAs?: ElementType;
  onCancel: () => void;
  onConfirm?: () => void;
  action?: CommunitySchedulePickerProps['action'];
  isSubmitting?: boolean;
  /** Focuses the title on mount, for when the field takes no focus, so a screen reader says where the picker opened. */
  focusTitle?: boolean;
  queuedCount?: number;
  onViewQueue?: () => void;
  children?: ReactNode;
}

const confirmLabelKey = (action: CommunitySchedulePickerProps['action'], isSubmitting: boolean) => {
  if (action !== 'schedule') return 'shared.cta.confirm';
  return isSubmitting ? 'community.composer.schedule.submitting' : 'community.composer.schedule.submit';
};

// Stable, so the title takes the focus once, when it mounts.
const focusOnMount = (element: HTMLElement | null) => element?.focus();

const pillClassName =
  'inline-flex h-10 items-center justify-center rounded-full px-5 text-[13px] font-medium leading-4 tracking-[-0.13px] transition-opacity';

// The picker's header and footer, also shown while its chunk loads and when it fails to load. It has no close button:
// the arrow cancels like the footer's Cancel, back to the composer.
export const SchedulePickerFrame: FC<SchedulePickerFrameProps> = ({
  translate,
  titleAs: Title = 'h2',
  onCancel,
  onConfirm,
  action,
  isSubmitting = false,
  focusTitle = false,
  queuedCount,
  onViewQueue,
  children,
}) => {
  const titleId = useId();
  const queueLabel = queuedCount
    ? translate('community.composer.schedule.view_queue_count', { count: queuedCount })
    : translate('community.composer.schedule.view_queue');
  const shortQueueLabel = queuedCount
    ? translate('community.composer.schedule.view_queue_short_count', { count: queuedCount })
    : translate('community.composer.schedule.queue.title');

  return (
    // Named after the title, so a screen reader says where the focus went when the picker replaces the composer.
    <ComposerFrame role="group" aria-labelledby={titleId}>
      <ComposerFrameHeader onBack={onCancel} backLabel={translate('shared.cta.back')}>
        <Title
          id={titleId}
          ref={focusTitle ? focusOnMount : undefined}
          tabIndex={-1}
          className="truncate text-[15px] font-semibold leading-5 text-black focus:outline-none"
        >
          {translate('community.composer.schedule.title')}
        </Title>
      </ComposerFrameHeader>
      <ComposerFrameBody>
        {/* avoid layout shift as children will be lazy-loaded */}
        <div className="flex min-h-[182px] flex-col gap-3">{children}</div>
      </ComposerFrameBody>
      <ComposerFrameFooter>
        {onViewQueue ? (
          <button
            type="button"
            onClick={onViewQueue}
            className="mr-3 min-w-0 pl-1 text-left text-[13px] font-medium leading-4 text-black/60 transition-colors hover:text-black"
          >
            {/* A phone gets the short link, which fits beside the buttons. */}
            <span className="sm:hidden">{shortQueueLabel}</span>
            <span className="hidden sm:inline">{queueLabel}</span>
          </button>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className={cn(pillClassName, 'border border-black/10 bg-white text-black hover:opacity-90')}
          >
            {translate('shared.cta.cancel')}
          </button>
          <button
            type="button"
            disabled={!onConfirm}
            onClick={onConfirm}
            className={cn(
              pillClassName,
              'bg-[var(--community-primary,#000)] text-[var(--community-text-on-primary,#fff)]',
              { 'hover:opacity-90': onConfirm, 'opacity-50 cursor-not-allowed': !onConfirm },
            )}
          >
            {translate(confirmLabelKey(action, isSubmitting))}
          </button>
        </div>
      </ComposerFrameFooter>
    </ComposerFrame>
  );
};
